/** Country of the date conventions of a language when the office gives none, or none the runtime knows. */
const DEFAULT_REGIONS: Record<string, string> = { fr: 'FR', en: 'GB' }

/** Countries are stored by their ISO 3166 code in lower case, except the United Kingdom stored as "uk". */
const REGION_ALIASES: Record<string, string> = { UK: 'GB' }

const displayLocales = new Map<string, string>()

const toRegion = (countryId?: string | null) => {
  const code = String(countryId || '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(code))
    return null
  return REGION_ALIASES[code] || code
}

/** Whether the runtime formats dates for this language and country, instead of falling back on the bare language. */
const isKnownLocale = (locale: string) => {
  try {
    return new Intl.DateTimeFormat(locale).resolvedOptions().locale.includes('-')
  }
  catch {
    return false
  }
}

/**
 * Locale to display dates to a user: the words in their language, the order, separators and clock of their office's
 * country. An English user reads 01/10/2026 in a London office and 10/1/2026 in a New York one, a French user
 * 01.10.26 in a Geneva office. Falls back on the default country of the language when there is no office or when the
 * runtime has no conventions for the pair (English in Morocco).
 */
export const getDisplayLocale = (language?: string | null, countryId?: string | null): string => {
  const lang = String(language || 'fr').slice(0, 2).toLowerCase()
  const region = toRegion(countryId)
  const key = `${lang}|${region || ''}`
  const cached = displayLocales.get(key)
  if (cached)
    return cached

  const candidates = [region && `${lang}-${region}`, DEFAULT_REGIONS[lang] && `${lang}-${DEFAULT_REGIONS[lang]}`]
  const locale = candidates.find((candidate): candidate is string => !!candidate && isKnownLocale(candidate)) || lang
  displayLocales.set(key, locale)
  return locale
}
